import React, { useCallback, useId, useMemo } from "react"

import { Tooltip } from "@coval/ui/tooltip"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { TranscriptFilterConfig } from "@/types/metrics/metadata"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../../styles"
import type { FilterPreset } from "../types"
import { FilterList } from "./FilterList"

interface TranscriptFilterSectionProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

/**
 * Hook to manage transcript filter state
 */
function useTranscriptFilter(
  metricRecordData: MetricRecordData,
  onChange: (metric: MetricRecordData) => void,
) {
  const filterConfig = metricRecordData.metric_metadata?.transcript_filter

  const activePreset: FilterPreset = useMemo(() => {
    return filterConfig ? "custom" : "full"
  }, [filterConfig])

  const handlePresetChange = useCallback(
    (preset: FilterPreset) => {
      if (preset === "full") {
        const { transcript_filter: _transcriptFilter, ...restMetadata } =
          metricRecordData.metric_metadata || {}
        onChange({
          ...metricRecordData,
          metric_metadata: restMetadata,
        })
      } else if (preset === "custom" && activePreset !== "custom") {
        onChange({
          ...metricRecordData,
          metric_metadata: {
            ...metricRecordData.metric_metadata,
            transcript_filter: {
              include: [],
              exclude: [],
            },
          },
        })
      }
    },
    [metricRecordData, onChange, activePreset],
  )

  const handleFilterConfigChange = useCallback(
    (newConfig: TranscriptFilterConfig) => {
      // Always update the config - don't auto-remove when empty
      // User must explicitly click "Full" to remove filters
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metricRecordData.metric_metadata,
          transcript_filter: newConfig,
        },
      })
    },
    [metricRecordData, onChange],
  )

  return {
    filterConfig,
    activePreset,
    handlePresetChange,
    handleFilterConfigChange,
  }
}

/**
 * Compact toggle for transcript scope - designed to sit inline with other controls
 */
export const TranscriptFilterToggle: React.FC<TranscriptFilterSectionProps> = ({
  metricRecordData,
  onChange,
}) => {
  const { activePreset, handlePresetChange } = useTranscriptFilter(
    metricRecordData,
    onChange,
  )
  const scopeLabelId = useId()

  const presets: { key: FilterPreset; label: string }[] = [
    { key: "full", label: "Full" },
    { key: "custom", label: "Custom" },
  ]

  return (
    <div className={`${FIELD_CONTAINER_STYLES} flex-1`}>
      <div className={LABEL_ROW_STYLES}>
        <span id={scopeLabelId} className={LABEL_STYLES}>
          Transcript Scope
        </span>
        <Tooltip
          content={
            <div className="max-w-xs p-1">
              <p className="text-sm">
                Choose which parts of the conversation to evaluate. Use filters
                to focus on specific messages or speakers.
              </p>
            </div>
          }
        >
          <Info className={INFO_ICON_STYLES} />
        </Tooltip>
      </div>

      <div
        role="group"
        aria-labelledby={scopeLabelId}
        className="flex h-control rounded-md bg-surface-1 p-1"
      >
        {presets.map((preset) => (
          <button
            key={preset.key}
            type="button"
            aria-label={`Transcript scope ${preset.label}`}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handlePresetChange(preset.key)
            }}
            className={`flex-1 px-3 text-sm font-medium rounded-sm transition-all cursor-pointer ${
              activePreset === preset.key
                ? "bg-surface-default text-coval-text shadow-sm"
                : "text-coval-text-muted hover:text-coval-text"
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Expanded filter details - shows when custom is selected
 */
export const TranscriptFilterDetails: React.FC<
  TranscriptFilterSectionProps
> = ({ metricRecordData, onChange }) => {
  const { filterConfig, activePreset, handleFilterConfigChange } =
    useTranscriptFilter(metricRecordData, onChange)

  if (activePreset !== "custom") {
    return null
  }

  return (
    <div className="w-full">
      <FilterList
        config={filterConfig || { include: [], exclude: [] }}
        onChange={handleFilterConfigChange}
      />
    </div>
  )
}
